import { Alert, App, Button } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { MdOutlineCheckCircle, MdOutlineRemoveCircleOutline } from 'react-icons/md'

import { Form, FormField } from '@/Core/UI/Form'
import { FieldSection } from '@/Ship/Components/EntityCard'
import { Hint } from '@/Ship/Components/Hint'
import { classNames } from '@/Ship/Helpers/classNames'

import { GMAIL_PASSWORD_TEXTS, UserGmailPasswordManager } from '../../Stores/UserGmailPasswordManager'

import type { UserFormContextLike } from '../../Contracts/IUser'

import styles from '../UserCard.module.css'

const FIELDS = {
    gmailAppPassword: {
        label: 'Пароль додатку в google workspace',
        type: 'password',
        isRequired: true,
        inputProps: { autoComplete: 'new-password' },
    },
}

interface SubmitArgs {
    values: Record<string, unknown>
}

/**
 * Вкладка «Пароль Google Workspace» (С3.1, D-04).
 *
 * Пароль лише записується: бек його не віддає ніколи, тож тут видно тільки
 * стан «задано / не задано» (`hasGmailAppPassword`). Після збереження поле
 * очищається — показати пароль удруге нема звідки, і так задумано.
 * Видно з правом `update_gmail_app_password`: супер адміністратор — будь-кому,
 * адміністратор — собі.
 */
export const UserGmailPasswordTab = observer(({ formContext }: { formContext: UserFormContextLike }) => {
    const { notification } = App.useApp()
    const [manager] = useState(() => new UserGmailPasswordManager(formContext.values?.hasGmailAppPassword === true))
    const [formKey, setFormKey] = useState(0)
    const uuid = typeof formContext.values?.uuid === 'string' ? formContext.values.uuid : undefined

    const onSubmit = async ({ values }: SubmitArgs): Promise<void> => {
        if (!uuid) return

        const value = typeof values['gmailAppPassword'] === 'string' ? values['gmailAppPassword'] : ''
        const saved = await manager.submit(uuid, value)

        if (!saved) return

        // Нова форма замість очищення старої: ядровий `Form` тримає контекст
        // на весь час життя компонента, і порожні значення надійніше дає новий
        setFormKey((key) => key + 1)
        formContext.setInitialValue?.('hasGmailAppPassword', manager.hasPassword)
        notification.success({ title: GMAIL_PASSWORD_TEXTS.saved, placement: 'topRight' })
    }

    return (
        <FieldSection title="Пароль Google Workspace">
            <div className={classNames(styles['stack'], styles['tabForm'])}>
                <Hint id="team.user.gmail">
                    Пароль додатку Google Workspace — 16 символів; вводьте його без пробілів, якими Google розбиває
                    пароль на групи. Після збереження пароль не видно, а без нього листи від імені співробітника не
                    відправляються.
                </Hint>

                <span
                    className={classNames(
                        styles['state'],
                        manager.hasPassword ? styles['stateSet'] : styles['stateUnset']
                    )}
                >
                    {manager.hasPassword ? <MdOutlineCheckCircle /> : <MdOutlineRemoveCircleOutline />}
                    {manager.hasPassword ? GMAIL_PASSWORD_TEXTS.set : GMAIL_PASSWORD_TEXTS.unset}
                </span>

                <Form key={formKey} fields={FIELDS} autoComplete="off" onSubmit={onSubmit}>
                    <FormField name="gmailAppPassword" />

                    {manager.error ? <Alert type="error" showIcon title={manager.error} /> : null}

                    <div className={classNames(styles['formButtons'])}>
                        <Button type="primary" htmlType="submit" loading={manager.isLoading}>
                            Зберегти
                        </Button>
                    </div>
                </Form>
            </div>
        </FieldSection>
    )
})
